/**
 * Helper utilities for atoms/CustomHTML
 *  – Termly cleanup
 *  – HubSpot helpers
 *  – Tock helpers
 */

/* ──────────────────────────  Termly  ────────────────────────── */
// Remove any Termly’s leftover loader script + old policy markup. (<script id="termly-jssdk">) and stale markup
export function cleanTermly(root) {
  // Remove global loader so Termly can re-insert it on the new page
  document.getElementById("termly-jssdk")?.remove()

  // Remove previously rendered DOM element with class .termly-policy from the container (root)
  root?.querySelectorAll(".termly-policy").forEach(node => node.remove())
}

/* ──────────────────────  HubSpot and Generic Embeds  ─────────────────── */
// This function queue every inline <script> that calls hbspt.forms.create(...) and run them after loader is ready
function runWhenHbsptReady(callback) {
  if (window.hbspt && window.hbspt.forms) {
    callback() // run loader callback already present (defined on window.hbspt)
  } else {
    // add loader to global array using hbspt one-time event listener
    window.__hsOnReady = window.__hsOnReady || []
    window.__hsOnReady.push(callback)
  }
}

/* ─────────────────────────────── Tock helpers ─────────────────────────────── */

/**
 * Function will try to pull the Tock “business slug” from a widget snippet from
 * the HTML attribute form (data-tock-business="slug") or from the JS initialiser (tock('init','slug'))
 * Returns `null` if no slug is found.
 */
export function extractTockSlug(html = "") {
  const matchAttribute = html.match(
    /data-tock-business\s*=\s*["']([^"']+)["']/i
  )
  if (matchAttribute) return matchAttribute[1]
  const matchInline = html.match(
    /tock\(\s*['"]init['"]\s*,\s*['"]([^'"]+)['"]/i
  )
  if (matchInline) return matchInline[1]
  return null
}

/**
 * Forces a clean reload of the Tock script when navigating inside an SPA.
 * It removes any existing Tock globals and their queued calls and Inserts a fresh copy of the vendor loader.
 * Fires a synthetic `window.load` event once the loader arrives that mimicking the first-page-load behaviour expected by the widget.
 * Immediately queues `tock('init', slug)` and the stub buffers this call until the real library has finished loading.
 */
export function hardReloadTock(slug) {
  if (!slug) return

  /* delete previous instance */
  delete window.tock
  delete window._tock
  document
    .querySelectorAll('script[src*="exploretock.com/tock.js"]')
    .forEach(script => script.remove())

  /* re-inject the official vendor stub + loader */
  ;(function (global, doc, tagName, srcUrl) {
    if (!global.tock) {
      const stubFunction = (global.tock = function () {
        stubFunction.callMethod
          ? stubFunction.callMethod.apply(stubFunction, arguments)
          : stubFunction.queue.push(arguments)
      })
      global._tock || (global._tock = stubFunction)
      stubFunction.push = stubFunction
      stubFunction.loaded = !0
      stubFunction.version = "1.0"
      stubFunction.queue = []

      const loaderScript = doc.createElement(tagName)
      loaderScript.async = !0
      loaderScript.src = srcUrl

      /* once the REAL library arrives, fake a late-load event
         so Tock runs the code path it normally runs on first page load */
      loaderScript.onload = () => {
        window.dispatchEvent(new Event("load")) // this will mimic a window.load event
      }

      const firstScript = doc.getElementsByTagName(tagName)[0]
      firstScript.parentNode.insertBefore(loaderScript, firstScript)
    }
  })(window, document, "script", "https://www.exploretock.com/tock.js")

  /* queue / call init immediately – the stub will buffer until ready */
  window.tock && window.tock("init", slug)
}

/**
 * Re‑execute / Force every <script> inside our snippet `fragment` to execute again on client‑side navigation
 * ‑ Remove any prior instance of HubSpot form embeds to avoid the “loader already present” no‑op
 * ‑ Finally, clone and replace each script so the browser treats it as new and runs it from scratch
 * - except the Tock snippets
 */
export function reExecuteScripts(root) {
  const scriptTags = root.querySelectorAll("script")

  scriptTags.forEach(oldScript => {
    const scriptSrc = oldScript.src || ""
    const isTockInline = /tock\(['"]init/.test(oldScript.textContent || "")
    const isTockExternal = /exploretock\.com\/tock\.js/i.test(scriptSrc)

    /* Skip Tock snippets since that is handled by hardReloadTock */
    if (isTockInline || isTockExternal) return

    const freshScript = document.createElement("script")

    // Remove a previous copy with same id or src (e.g. HubSpot) to avoid “only once” error
    if (oldScript.id) document.getElementById(oldScript.id)?.remove()
    if (oldScript.src) {
      document
        .querySelectorAll(`script[src="${oldScript.src}"]`)
        .forEach(script => script.remove())
    }

    // Copy every attribute
    Array.from(oldScript.attributes).forEach(attr =>
      freshScript.setAttribute(attr.name, attr.value)
    )

    // HubSpot inline vs external loader coordination
    const isHubspotLoader = oldScript.src?.includes("/forms/embed/v2.js")
    const isInlineHubspot = /hbspt\.forms\.create/i.test(oldScript.textContent)

    if (isHubspotLoader) {
      freshScript.onload = () => {
        // When the loader finishes loading, fire all queued inline calls
        ;(window.__hsOnReady || []).splice(0).forEach(fn => fn())
      }
      freshScript.async = true // script will load async to prevent blocking the page while hubspot content is downloading
    }

    if (isInlineHubspot) {
      // defer execution function within runWhenHbsptReady until the HubSpot loader (window.hbspt) is available
      runWhenHbsptReady(() => {
        // once ready, recreate and reinsert the original inline script to trigger form rendering
        const inlineClone = document.createElement("script")
        inlineClone.text = oldScript.textContent
        oldScript.parentNode.replaceChild(inlineClone, oldScript)
      })

      return // return early to skip the default script replacement for HubSpot forms embeds
    }

    // This only apply to non-HubSpot form <script> embed
    freshScript.text = oldScript.textContent
    // Swapping nodes in order to execute immediately when added to the DOM
    oldScript.parentNode.replaceChild(freshScript, oldScript)
  })
}
